/**
 * Step 7: Call sizing recommendation service
 * If recommended width or height is larger, update tunnel sizes
 * Each tunnel uses its own airflow-specific ahuParamsResult
 */
import { dispatchMutation } from '../../../../utils/dispatch.utils';
import { ahuApi } from '../../../../services';
import { DEFAULT_COEFFICIENTS } from '../../../../models';

export async function step7ApplySizingRecommendations(
  tunnels: any[],
  tunnelIds: number[],
  tunnelAhuParamsResults: any[] | any, // Array of ahuParamsResult per tunnel, or single value for backward compatibility
  underInsulationId: number,
  underlinerId: number,
  outerlinerId: number,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<any[]> {
  const updatedTunnels: any[] = [];

  for (let i = 0; i < tunnels.length; i++) {
    const tunnel = tunnels[i];
    // Get this tunnel's airflow-specific ahuParamsResult
    const ahuParamsResult = Array.isArray(tunnelAhuParamsResults)
      ? tunnelAhuParamsResults[i]
      : tunnelAhuParamsResults;

    // Skip if no valid ahuParamsResult for this tunnel
    if (!ahuParamsResult?.current_width && !ahuParamsResult?.current_height) {
      updatedTunnels[i] = tunnel;
      continue;
    }

    const recommendedWidth = ahuParamsResult?.current_width || 0;
    const recommendedHeight = ahuParamsResult?.current_height || 0;

    const newWidth = Math.max(tunnel.width, recommendedWidth);
    const newHeight = Math.max(tunnel.height, recommendedHeight);

    if (newWidth !== tunnel.width || newHeight !== tunnel.height) {
      updatedTunnels[i] = await dispatchMutation(
        ahuApi.endpoints.updateAhuTunnel,
        {
          id: tunnelIds[i],
          body: { width: newWidth, height: newHeight },
        },
        getOptions(),
      );
    } else {
      updatedTunnels[i] = tunnel;
    }
    if (tunnel.floor_id && underInsulationId && underlinerId) {
      await dispatchMutation(
        ahuApi.endpoints.updateTunnelFloor,
        {
          id: tunnel.floor_id,
          body: {
            under_insulation_id: underInsulationId,
            underliner_id: underlinerId,
          },
        },
        getOptions(),
      );
    }
    if (tunnel.casing_id && outerlinerId) {
      await dispatchMutation(
        ahuApi.endpoints.updateTunnelCasing,
        {
          id: tunnel.casing_id,
          body: {
            outerliner_id: outerlinerId,
          },
        },
        getOptions(),
      );
    }
    onProgress?.(DEFAULT_COEFFICIENTS.update_tunnel_floor_casing); // update_tunnel coefficient
  }

  return updatedTunnels;
}
